import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  ChatMessageCurrentState,
  Notification,
  NotificationStatus,
} from '../notifications';
import {
  fetchChats,
  fetchMessages,
  hideConversation,
  readConversation,
  sendMessage,
} from './actions';
import { ConversationPreview } from './model';
import { Chatbox } from './model/Chatbox';

export interface ConversationsState {
  conversationPreviews: ConversationPreview[];
  chatbox: Chatbox;
  loadingConversations: boolean;
  loadingMessages: boolean;
  loadingReadingConversation: boolean;
  isConversationDrawerOpen: boolean;
  searchInChatsDrawer: string;
  showCompanionsInChatsDrawer: boolean;
}

const initialChatbox: Chatbox = {
  companionId: undefined,
  lastActive: undefined,
  open: false,
  messages: { data: [] },
  chatId: undefined,
  hasDefaultPic: undefined,
  toMemberId: undefined,
  fromMemberId: undefined,
  toMemberDisplayName: undefined,
  fromMemberDisplayName: undefined,
};

const initialState: ConversationsState = {
  conversationPreviews: [],
  chatbox: initialChatbox,
  loadingConversations: false,
  loadingMessages: false,
  loadingReadingConversation: false,
  isConversationDrawerOpen: false,
  searchInChatsDrawer: '',
  showCompanionsInChatsDrawer: false,
};

const normalizeChatKey = (value: unknown) =>
  value === null || value === undefined
    ? ''
    : String(value).trim().toLowerCase();

const conversationsSlice = createSlice({
  name: 'conversations',
  initialState,
  reducers: {
    showConversationDrawer: (state) => {
      state.isConversationDrawerOpen = true;
    },
    hideConversationDrawer: (state) => {
      state.isConversationDrawerOpen = false;
    },
    setSearchInChatsDrawer: (state, action: PayloadAction<string>) => {
      state.searchInChatsDrawer = action.payload;
    },
    setShowCompanionsInChatsDrawer: (state, action: PayloadAction<boolean>) => {
      state.showCompanionsInChatsDrawer = action.payload;
    },
    showChatbox: (
      state,
      action: PayloadAction<{
        companionId?: string;
        picUrl?: string;
        lastActive?: string;
        fromDrawer?: boolean;
        chatId?: string;
        hasDefaultPic?: boolean;
        toMemberId?: string;
        fromMemberId?: string;
        toMemberDisplayName?: string;
        fromMemberDisplayName?: string;
        roomName?: string;
      }>
    ) => {
      state.chatbox.companionId = action.payload.companionId;
      state.chatbox.picUrl = action.payload.picUrl;
      state.chatbox.lastActive = action.payload.lastActive;
      state.chatbox.open = true;
      state.chatbox.fromDrawer = action.payload.fromDrawer;
      state.chatbox.hasDefaultPic = action.payload.hasDefaultPic;
      if (state.chatbox.chatId !== action.payload.chatId) {
        state.chatbox.messages = initialChatbox.messages;
      }
      state.chatbox.chatId = action.payload.chatId;
      state.chatbox.fromMemberDisplayName =
        action.payload.fromMemberDisplayName;
      state.chatbox.toMemberDisplayName = action.payload.toMemberDisplayName;
      state.chatbox.fromMemberId = action.payload.fromMemberId;
      state.chatbox.toMemberId = action.payload.toMemberId;
      state.chatbox.roomName = action.payload.roomName;
    },
    closeChatbox: (state) => {
      const lastChatId = state.chatbox.chatId;
      state.chatbox = initialChatbox;

      const normalizedLastChatId = normalizeChatKey(lastChatId);
      if (!normalizedLastChatId) return;

      // If the user was in a chat, treat it as read locally so the tray UI
      // doesn't show unread indicators for messages already seen in the open chat.
      state.conversationPreviews = state.conversationPreviews.map((preview) =>
        normalizeChatKey(preview.chatId) === normalizedLastChatId
          ? {
              ...preview,
              lastChatMessageStatusFromMember: NotificationStatus.read,
              lastChatMessageStatusToMember: NotificationStatus.read,
            }
          : preview
      );
    },
    addChatboxMessage: (state, action: PayloadAction<Notification>) => {
      if (!state.chatbox.open) return;

      const norm = (v?: unknown) =>
        v === null || v === undefined ? '' : String(v).trim().toLowerCase();

      const incoming = action.payload as Notification & { chatId?: string };
      const incomingChatId = norm(incoming.chatId ?? incoming.title);
      const activeChatId = norm(state.chatbox.chatId);
      const activeCompanionId = norm(state.chatbox.companionId);
      const incomingFrom = norm(incoming.fromMemberId);
      const incomingTo = norm(incoming.toMemberId);

      const isActiveChat =
        Boolean(activeChatId) && incomingChatId === activeChatId;
      const isActiveCompanion =
        Boolean(activeCompanionId) &&
        (activeCompanionId === incomingFrom ||
          activeCompanionId === incomingTo);

      if (!isActiveChat && !isActiveCompanion) return;

      const existingIndex = state.chatbox.messages?.data?.findIndex(
        (m) => norm(m.id) === norm(incoming.id)
      );

      // If we already have this message (often the local pending placeholder),
      // merge fields and upgrade state instead of dropping.
      if (
        existingIndex !== undefined &&
        existingIndex !== -1 &&
        state.chatbox.messages
      ) {
        const existing = state.chatbox.messages.data[existingIndex];
        state.chatbox.messages.data[existingIndex] = {
          ...existing,
          ...incoming,
          currentState: ChatMessageCurrentState.SENT,
        };
        return;
      }

      state.chatbox.messages = {
        ...state.chatbox.messages,
        data: [incoming, ...(state.chatbox.messages?.data ?? [])],
      };
    },

    markConversationPreviewUnread: (
      state,
      action: PayloadAction<{
        chatId: string;
        lastChatMessage?: string;
        lastChatMessageDate?: string;
      }>
    ) => {
      const normalizedChatId = normalizeChatKey(action.payload.chatId);
      if (!normalizedChatId) return;

      state.conversationPreviews = state.conversationPreviews.map((preview) =>
        normalizeChatKey(preview.chatId) === normalizedChatId
          ? {
              ...preview,
              lastChatMessage:
                action.payload.lastChatMessage ?? preview.lastChatMessage,
              lastChatMessageDate:
                action.payload.lastChatMessageDate ??
                preview.lastChatMessageDate,
              // We don't reliably know whether the current user is "from" or "to" here,
              // so set both to unread. This ensures header/tray indicators update
              // immediately and will be corrected by the next fetchChats/readConversation.
              lastChatMessageStatusFromMember: NotificationStatus.unread,
              lastChatMessageStatusToMember: NotificationStatus.unread,
            }
          : preview
      );
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchChats.pending, (state) => {
        state.loadingConversations = true;
      })
      .addCase(fetchChats.fulfilled, (state, action) => {
        state.loadingConversations = false;
        if (action.payload) {
          const previousByChatId = new Map(
            state.conversationPreviews
              .filter((p) => normalizeChatKey(p.chatId))
              .map((p) => [normalizeChatKey(p.chatId), p] as const)
          );

          state.conversationPreviews = action.payload.map((incoming) => {
            const chatIdKey = normalizeChatKey(incoming.chatId);
            const previous = chatIdKey
              ? previousByChatId.get(chatIdKey)
              : undefined;

            // If we already marked this conversation as read locally (via `readConversation`
            // or `closeChatbox`), don't let a stale fetch response flip it back to unread
            // for the same last message.
            if (previous && chatIdKey) {
              const sameLastMessage =
                normalizeChatKey(previous.lastChatMessageDate) ===
                normalizeChatKey(incoming.lastChatMessageDate);

              const previousWasRead =
                previous.lastChatMessageStatusFromMember ===
                  NotificationStatus.read &&
                previous.lastChatMessageStatusToMember ===
                  NotificationStatus.read;

              const incomingShowsUnread =
                incoming.lastChatMessageStatusFromMember ===
                  NotificationStatus.unread ||
                incoming.lastChatMessageStatusToMember ===
                  NotificationStatus.unread;

              if (sameLastMessage && previousWasRead && incomingShowsUnread) {
                return {
                  ...incoming,
                  lastChatMessageStatusFromMember: NotificationStatus.read,
                  lastChatMessageStatusToMember: NotificationStatus.read,
                };
              }
            }

            return incoming;
          });
        }

        const activeChatId = normalizeChatKey(state.chatbox?.chatId);
        if (state.chatbox?.open && activeChatId) {
          // The server can momentarily return unread state due to timing; ensure
          // the currently open chat never shows as unread in the tray.
          state.conversationPreviews = state.conversationPreviews.map(
            (preview) =>
              normalizeChatKey(preview.chatId) === activeChatId
                ? {
                    ...preview,
                    lastChatMessageStatusFromMember: NotificationStatus.read,
                    lastChatMessageStatusToMember: NotificationStatus.read,
                  }
                : preview
          );
        }
      })
      .addCase(fetchChats.rejected, (state) => {
        state.loadingConversations = false;
      })
      .addCase(fetchMessages.pending, (state) => {
        state.loadingMessages = true;
      })
      .addCase(fetchMessages.fulfilled, (state, action) => {
        let messagesList = state.chatbox.messages?.data || [];
        if (action.meta.arg.xMsContinuationToken) {
          messagesList.push(...action.payload.data);
        } else {
          messagesList = [...action.payload.data];
        }
        state.chatbox.messages = {
          data: messagesList,
          xMsContinuationToken: action.payload?.xMsContinuationToken,
        };
        state.loadingMessages = false;
      })
      .addCase(fetchMessages.rejected, (state) => {
        state.loadingMessages = false;
      })
      .addCase(sendMessage.pending, (state, action) => {
        if (action.meta.arg.resend) {
          const resendMessageIndex = state.chatbox.messages?.data.findIndex(
            (message) => message.id === action.meta.arg.messageId
          );
          if (
            resendMessageIndex !== undefined &&
            resendMessageIndex !== -1 &&
            state.chatbox.messages
          ) {
            state.chatbox.messages.data[resendMessageIndex].currentState =
              ChatMessageCurrentState.PENDING;
          }
        } else {
          const newMessage: Notification = {
            content: action.meta.arg.newChatMessage.chatmessage,
            title: action.meta.arg.chatId ?? '',
            toMemberId: action.meta.arg.toMemberId,
            currentState: ChatMessageCurrentState.PENDING,
            id: action.meta.arg.messageId ?? '',
            type: '',
            fromMemberId: '',
            fromMemberPicUrl: '',
            statusFromMember: '',
            statusToMember: '',
            reason: '',
            priority: 0,
            dateCreated: new Date().toISOString(),
            dateUpdated: '',
            category: 0,
          };
          state.chatbox.messages = {
            ...state.chatbox.messages,
            data: [newMessage, ...(state.chatbox.messages?.data ?? [])],
          };
        }
      })
      .addCase(sendMessage.fulfilled, (state, action) => {
        if (action.meta.arg.resend) {
          const resendMessageIndex = state.chatbox.messages?.data.findIndex(
            (message) => message.id === action.meta.arg.messageId
          );
          if (
            resendMessageIndex !== undefined &&
            resendMessageIndex !== -1 &&
            state.chatbox.messages
          ) {
            state.chatbox.messages.data[resendMessageIndex] = {
              ...action.payload[0],
              currentState: ChatMessageCurrentState.SENT,
            };
          }
        } else {
          const payloadMessage = action.payload?.[0];
          if (!payloadMessage) return;

          const newMessage: Notification = {
            ...payloadMessage,
            currentState: ChatMessageCurrentState.SENT,
          };

          if (!state.chatbox.messages) {
            state.chatbox.messages = { data: [newMessage] };
            return;
          }

          const placeholderId = action.meta.arg.messageId;

          // Remove the local pending placeholder (don’t assume it is index 0).
          if (placeholderId) {
            const idx = state.chatbox.messages.data.findIndex(
              (m) => m.id === placeholderId
            );
            if (idx !== -1) {
              state.chatbox.messages.data.splice(idx, 1);
            }
          }

          // De-dupe if SignalR already inserted this message.
          const alreadyExists = state.chatbox.messages.data.some(
            (m) => m.id === newMessage.id
          );
          if (!alreadyExists) {
            state.chatbox.messages.data.unshift(newMessage);
          }
        }
      })
      .addCase(sendMessage.rejected, (state, action) => {
        if (action.meta.arg.resend) {
          const resendMessageIndex = state.chatbox.messages?.data.findIndex(
            (message) => message.id === action.meta.arg.messageId
          );
          if (
            resendMessageIndex !== undefined &&
            resendMessageIndex !== -1 &&
            state.chatbox.messages
          ) {
            state.chatbox.messages.data[resendMessageIndex].currentState =
              ChatMessageCurrentState.FAILED;
          }
        } else {
          if (!state.chatbox.messages) return;

          const placeholderId = action.meta.arg.messageId;
          const idx = placeholderId
            ? state.chatbox.messages.data.findIndex(
                (m) => m.id === placeholderId
              )
            : -1;

          if (idx !== -1) {
            state.chatbox.messages.data[idx].currentState =
              ChatMessageCurrentState.FAILED;
            return;
          }

          // Fallback: mark the most recent pending message as failed.
          const fallbackIdx = state.chatbox.messages.data.findIndex(
            (m) => m.currentState === ChatMessageCurrentState.PENDING
          );
          if (fallbackIdx !== -1) {
            state.chatbox.messages.data[fallbackIdx].currentState =
              ChatMessageCurrentState.FAILED;
          }
        }
      })
      .addCase(readConversation.pending, (state) => {
        state.loadingReadingConversation = true;
      })
      .addCase(readConversation.fulfilled, (state, action) => {
        state.loadingReadingConversation = false;

        // `readConversation` is dispatched with an object arg like `{ chatId, clientAction }`.
        // Previously we compared `conversationPreview.chatId` to `action.meta.arg` (object),
        // which prevented the preview from ever being marked as read.
        const chatId =
          typeof (action.meta.arg as any) === 'string'
            ? (action.meta.arg as any)
            : (action.meta.arg as any)?.chatId;

        if (!chatId) {
          return;
        }

        state.conversationPreviews = state.conversationPreviews.map(
          (conversationPreview) =>
            conversationPreview.chatId === chatId
              ? {
                  ...conversationPreview,
                  // The API call is “mark this conversation read” for the current user.
                  // We don't have the current user's memberId in this slice, so we mark both
                  // status fields as read to ensure the UI clears immediately.
                  lastChatMessageStatusFromMember: NotificationStatus.read,
                  lastChatMessageStatusToMember: NotificationStatus.read,
                }
              : conversationPreview
        );
      })
      .addCase(readConversation.rejected, (state) => {
        state.loadingReadingConversation = false;
      })
      .addCase(hideConversation.fulfilled, (state, action) => {
        const chatId =
          typeof (action.meta.arg as any) === 'string'
            ? (action.meta.arg as any)
            : (action.meta.arg as any)?.chatId;

        if (!chatId) {
          return;
        }

        state.conversationPreviews = state.conversationPreviews.filter(
          (conversationPreview) => conversationPreview.chatId !== chatId
        );
      });
  },
});

export const {
  showConversationDrawer,
  hideConversationDrawer,
  showChatbox,
  closeChatbox,
  addChatboxMessage,
  markConversationPreviewUnread,
  setSearchInChatsDrawer,
  setShowCompanionsInChatsDrawer,
} = conversationsSlice.actions;

export default conversationsSlice.reducer;
